import React from 'react';
import Dialog from '@react/react-spectrum/Dialog';
import Link from '@react/react-spectrum/Link';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useRenewalStatusPolling } from '../../hooks/useRenewalStatusPolling';
import { useFreshnessLabel } from '../../hooks/useFreshnessLabel';
import { getStepperNodes, isRenewalStatusTerminal, reduceRenewalEvents } from '../../service/RenewalStatusService';
import RenewalStatusStepper from '../RenewalStatusStepper/RenewalStatusStepper';
import './RenewalStatusDialog.css';

interface RenewalStatusDialogProps {
  accountId: string;
  isOpen: boolean;
  onClose: () => void;
}

// A centered modal popup rather than a side drawer — a right-hand drawer would push this
// already-wide records page further right and force horizontal scrolling.
function RenewalStatusDialog(props: Readonly<RenewalStatusDialogProps>): React.ReactElement {
  const { accountId, isOpen, onClose } = props;
  const { status, isPollingStopped, refresh } = useRenewalStatusPolling(accountId, 'detail', isOpen);
  const { state } = reduceRenewalEvents(status?.events);
  const isTerminal = isRenewalStatusTerminal(state);

  const [lastUpdatedAt, setLastUpdatedAt] = React.useState<number | undefined>(undefined);
  // Whether this dialog session has ever observed a non-terminal status. If a renewal
  // was already finished before the dialog was even opened, there's nothing to time —
  // the freshness label is suppressed entirely rather than showing a ticking timer that
  // implies polling is still happening for a result that's already final.
  const [wasEverNonTerminal, setWasEverNonTerminal] = React.useState(false);
  // A fresh poll tick returns a new `status` object reference even when the underlying
  // data hasn't actually changed (e.g. still just "Triggered" with the same timestamp).
  // Compare content, not reference, so "updated Xs ago" only resets on a real change.
  const lastContentRef = React.useRef<string | undefined>(undefined);

  React.useEffect(() => {
    if (!isOpen) {
      setLastUpdatedAt(undefined);
      setWasEverNonTerminal(false);
      lastContentRef.current = undefined;
      return;
    }
    if (!status) {
      return;
    }
    if (!isTerminal) {
      setWasEverNonTerminal(true);
    }
    // Event order is explicitly unspecified by the backend — sort before comparing, or
    // the same unchanged timeline coming back in a different order would look "new".
    const sortedEvents = [...(status.events ?? [])].sort(
      (a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime()
    );
    const content = JSON.stringify({ present: status.present, events: sortedEvents });
    if (content !== lastContentRef.current) {
      lastContentRef.current = content;
      setLastUpdatedAt(Date.now());
    }
  }, [status, isTerminal, isOpen]);

  const showFreshness = !isTerminal || wasEverNonTerminal;
  const freshnessLabel = useFreshnessLabel(showFreshness ? lastUpdatedAt : undefined, isTerminal);

  const renderContent = () => {
    if (!status) {
      return (
        <div className='renewal-status-dialog-loading'>
          <CircularProgress size={24} />
        </div>
      );
    }
    // present:false is valid data (no renewal history yet), not an error — it is only
    // paired with a "Refresh status" affordance when polling has actually given up
    // (isPollingStopped), never hidden behind `present` the way the old inline block did.
    if (!status.present) {
      return (
        <div className='renewal-status-dialog-empty'>
          {isPollingStopped ? (
            <>
              <p>We couldn&apos;t load the renewal status for this account.</p>
              <Link href='#' onClick={refresh}>Refresh status</Link>
            </>
          ) : (
            <p>No auto-renewal activity yet for this account.</p>
          )}
        </div>
      );
    }
    return (
      <>
        <RenewalStatusStepper nodes={getStepperNodes(status.events)} />
        {isPollingStopped && !isTerminal && (
          <div className='renewal-status-dialog-row'>
            <Link href='#' onClick={refresh}>Refresh status</Link>
          </div>
        )}
      </>
    );
  };

  return (
    <Dialog
      className='renewal-status-dialog'
      title={
        <div className='renewal-status-dialog-title'>
          <span>Auto-renewal status</span>
          {freshnessLabel && <span className='renewal-status-dialog-freshness'>{freshnessLabel}</span>}
        </div>
      }
      open={isOpen}
      isDismissible
      onClose={onClose}
    >
      {renderContent()}
    </Dialog>
  );
}

export default RenewalStatusDialog;
